import * as THREE from 'three';
import { GeoBuilder } from './geo.js';
import { rr, rnd, pick, chance, TAU } from './util.js';

/**
 * Parametric low-poly buildings. Everything is built in LOCAL space
 * (origin = centre of the footprint on the ground, front = +Z) inside a Kit that
 * separates geometry into batches with different materials.
 */

export const PALETTE = {
  walls: ['#f6e7c8', '#f8d98b', '#f5b895', '#a9d6ea', '#b6e2c6', '#f4b1c4', '#d3c2ec', '#f7f1e6', '#ffcf9e', '#e9c9a0'],
  roofs: ['#d9603b', '#c8443d', '#2a9d8f', '#4a6a9a', '#8a5a3c', '#e88a3a', '#7a4f9a', '#b9483c'],
  doors: ['#8b4a2b', '#2f5d8a', '#b23a48', '#3a7d5c', '#e0a030', '#6b3f8a'],
  shutters: ['#3a7d5c', '#2f5d8a', '#b23a48', '#e0a030', '#5a3b2b', '#6b3f8a'],
  wood: '#8b5a3c',
  woodDark: '#5b3a29',
  stone: '#a79f92',
  brick: '#b5533c',
};

export class Kit {
  constructor() {
    this.body = new GeoBuilder();
    this.lit = new GeoBuilder();
    this.dark = new GeoBuilder();
    this.glow = new GeoBuilder();
  }
  /** Copy another kit's parts into this one with a transform (rotation about Y). */
  absorb(other, x = 0, y = 0, z = 0, ry = 0) {
    const m = new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), ry), new THREE.Vector3(1, 1, 1));
    for (const k of ['body', 'lit', 'dark', 'glow']) {
      for (const g of other[k].parts) this[k].parts.push(g.clone().applyMatrix4(m));
    }
    return this;
  }
  buildAll() {
    return { body: this.body.build(), lit: this.lit.build(), dark: this.dark.build(), glow: this.glow.build() };
  }
}

const rot = (ox, oz, ry) => {
  const c = Math.cos(ry), s = Math.sin(ry);
  return [ox * c + oz * s, -ox * s + oz * c];
};

/** Window on a wall face. (cx,cy,cz) = centre on the wall surface, ry = outward facing (0 = +Z). */
export function addWindow(K, cx, cy, cz, ry, o = {}) {
  const w = o.w ?? 0.95, h = o.h ?? 1.2;
  const on = o.on ?? chance(0.7);
  const P = (ox, oz) => { const [dx, dz] = rot(ox, oz, ry); return [cx + dx, cz + dz]; };
  const frame = o.frame ?? '#fbf6ea';
  let [x, z] = P(0, 0.05);
  K.body.box(w + 0.26, h + 0.26, 0.1, x, cy - h / 2 - 0.13, z, frame, { ry });
  [x, z] = P(0, 0.1);
  const g = (on ? K.lit : K.dark);
  const bright = on ? 0.8 + rnd() * 0.4 : 1;
  g.box(w, h, 0.06, x, cy - h / 2, z, [bright, bright, bright].map((v) => v), { ry });
  [x, z] = P(0, 0.14);
  K.body.box(0.06, h, 0.05, x, cy - h / 2, z, frame, { ry });
  K.body.box(w, 0.06, 0.05, x, cy - 0.03, z, frame, { ry });
  [x, z] = P(0, 0.16);
  K.body.box(w + 0.42, 0.09, 0.3, x, cy - h / 2 - 0.22, z, '#d8d0c0', { ry });
  if (o.shutter) {
    for (const s of [-1, 1]) {
      [x, z] = P(s * (w / 2 + 0.34), 0.07);
      K.body.box(0.38, h + 0.1, 0.06, x, cy - h / 2 - 0.05, z, o.shutter, { ry });
    }
  }
  if (o.flowers) {
    [x, z] = P(0, 0.26);
    K.body.box(w + 0.1, 0.22, 0.3, x, cy - h / 2 - 0.42, z, '#7a5236', { ry });
    const fc = pick(['#ff6f91', '#ffd166', '#ffffff', '#c77dff']);
    for (let i = -2; i <= 2; i++) {
      const [fx, fz] = P(i * (w / 5), 0.27);
      K.body.ico(0.11, 0, fx, cy - h / 2 - 0.16, fz, i % 2 ? fc : '#4caf50', { jitter: 0.1 });
    }
  }
}

/** Gable roof (ridge along Z) with overhangs. wy = wall top height. */
export function gableRoof(K, w, d, wy, roofH, color, trim = null) {
  const half = w / 2;
  const ang = Math.atan2(roofH, half);
  const L = Math.hypot(half, roofH) + 0.42;
  for (const side of [-1, 1]) {
    const dx = side * Math.cos(ang), dy = -Math.sin(ang);
    const tc = (L - 0.12) / 2 - 0.06;
    K.body.box(L + 0.12, 0.22, d + 0.75, 0 + dx * tc, wy + roofH + dy * tc, 0, color, { rz: -side * ang, jitter: 0.07 });
    // roof edge trim
    if (trim) {
      const te = L - 0.06;
      K.body.box(0.14, 0.3, d + 0.8, dx * te, wy + roofH + dy * te - 0.02, 0, trim, { rz: -side * ang });
    }
  }
  K.body.box(0.34, 0.15, d + 0.8, 0, wy + roofH + 0.05, 0, color, { jitter: 0.1 });
}

function chimney(K, x, wy, roofH, z, top, color = '#a8543e') {
  K.body.box(0.75, top, 0.75, x, wy - 0.3, z, color, { jitter: 0.1 });
  K.body.box(0.95, 0.14, 0.95, x, wy - 0.3 + top, z, '#6b615a');
  return { x, y: wy - 0.3 + top + 0.15, z };
}

function door(K, x, w, z, ry, color, lampOn = true) {
  const P = (ox, oz) => { const [dx, dz] = rot(ox, oz, ry); return [x + dx, z + dz]; };
  let [px, pz] = P(0, 0.06);
  K.body.box(w + 0.3, 2.15, 0.1, px, 0.5, pz, '#fbf6ea', { ry });
  [px, pz] = P(0, 0.12);
  K.body.box(w, 2.0, 0.08, px, 0.5, pz, color, { ry });
  [px, pz] = P(w * 0.3, 0.2);
  K.body.ico(0.06, 0, px, 1.45, pz, '#f3d27a', { ry });
  [px, pz] = P(0, 0.45);
  K.body.box(w + 0.7, 0.18, 0.75, px, 0.32, pz, '#b7afa1', { ry });
  // canopy
  [px, pz] = P(0, 0.5);
  K.body.box(w + 0.9, 0.12, 0.95, px, 2.75, pz, '#8b5a3c', { ry, rx: 0.1 * Math.cos(ry * 0) });
  // lamp
  const [lx, lz] = P(-(w / 2 + 0.55), 0.14);
  K.body.box(0.08, 0.22, 0.08, lx, 1.95, lz, '#3b3f4f');
  K.glow.box(0.2, 0.26, 0.2, lx, 1.72, lz, '#ffffff', { ry });
}

/**
 * Generic cottage / townhouse. Returns metadata for placement.
 * opts: w, d, floors, wall, roof, doorColor, timber, shutter, chimneyCount, flowers
 */
export function buildHouse(opts = {}) {
  const K = new Kit();
  const floors = opts.floors ?? 1;
  const w = opts.w ?? rr(5.6, 7.2), d = opts.d ?? rr(5.0, 6.2);
  const H = 2.55 * floors;
  const roofH = opts.roofH ?? rr(1.9, 2.6);
  const wall = opts.wall ?? pick(PALETTE.walls);
  const roof = opts.roof ?? pick(PALETTE.roofs);
  const doorColor = opts.doorColor ?? pick(PALETTE.doors);
  const shutter = opts.shutter === undefined ? pick(PALETTE.shutters) : opts.shutter;
  const wy = 0.5 + H;

  // foundation + walls + gable
  K.body.box(w + 0.3, 1.2, d + 0.3, 0, -0.7, 0, '#9a9285', { jitter: 0.1 });
  K.body.box(w, H, d, 0, 0.5, 0, wall, { jitter: 0.03 });
  K.body.prism(w, roofH, d, 0, wy, 0, wall);
  gableRoof(K, w, d, wy, roofH, roof, opts.trim ?? '#fbf6ea');

  // timber frame
  if (opts.timber) {
    const beam = '#5b3a29';
    for (const z of [d / 2 + 0.035, -d / 2 - 0.035]) {
      for (const x of [-w / 2 + 0.1, 0, w / 2 - 0.1]) K.body.box(0.18, H, 0.08, x, 0.5, z, beam);
      for (let f = 0; f <= floors; f++) K.body.box(w, 0.2, 0.08, 0, 0.5 + f * 2.55 - (f === floors ? 0.2 : 0), z, beam);
      for (let f = 0; f < floors; f++) {
        for (const s of [-1, 1]) {
          const bw = w / 2 - 0.35, bh = 2.05;
          K.body.boxC(0.12, Math.hypot(bw, bh), 0.08, s * w * 0.25, 0.5 + f * 2.55 + 1.2, z, beam, { rz: -s * Math.atan2(bw, bh) });
        }
      }
    }
    for (const x of [w / 2 + 0.035, -w / 2 - 0.035]) {
      for (const z of [-d / 2 + 0.1, 0, d / 2 - 0.1]) K.body.box(0.08, H, 0.18, x, 0.5, z, beam);
      for (let f = 0; f <= floors; f++) K.body.box(0.08, 0.2, d, x, 0.5 + f * 2.55 - (f === floors ? 0.2 : 0), 0, beam);
    }
  }

  // door + windows
  const doorX = opts.doorX ?? 0;
  const dw = 1.05;
  door(K, doorX, dw, d / 2, 0, doorColor);
  const wo = { shutter, flowers: opts.flowers ?? chance(0.5), frame: opts.timber ? '#f3ead6' : '#fbf6ea' };
  for (let f = 0; f < floors; f++) {
    const cy = 0.5 + f * 2.55 + 1.55;
    const xs = f === 0 ? [-w * 0.3, w * 0.3] : [-w * 0.3, 0, w * 0.3];
    for (const x of xs) {
      if (f === 0 && Math.abs(x - doorX) < 1.2) continue;
      addWindow(K, x, cy, d / 2, 0, { ...wo, flowers: f === 0 && wo.flowers });
    }
    // back
    addWindow(K, -w * 0.25, cy, -d / 2, Math.PI, { ...wo, shutter: null, flowers: false });
    addWindow(K, w * 0.25, cy, -d / 2, Math.PI, { ...wo, shutter: null, flowers: false });
    // sides
    for (const sx of [-1, 1]) {
      const zs = d > 5.5 ? [-d * 0.24, d * 0.24] : [0];
      for (const z of zs) addWindow(K, sx * w / 2, cy, z, sx * Math.PI / 2, { ...wo, flowers: false, shutter: null });
    }
  }
  // attic window in the front gable
  addWindow(K, 0, wy + roofH * 0.32, d / 2, 0, { w: 0.7, h: 0.75, shutter: null, on: chance(0.6), frame: '#fbf6ea' });

  // chimney(s)
  const chimneys = [];
  const cx = (opts.chimneyX ?? rr(0.18, 0.28)) * w * (chance(0.5) ? 1 : -1);
  chimneys.push(chimney(K, cx, wy, roofH, rr(-0.2, 0.2) * d, roofH * (1 - Math.abs(cx) / (w / 2)) + 0.9));

  return { kit: K, w, d, height: wy + roofH, chimneys, doorLocal: { x: doorX, z: d / 2 + 1.4 } };
}

/** L-shaped house: main + perpendicular wing. */
export function buildLHouse(opts = {}) {
  const main = buildHouse({ ...opts, floors: opts.floors ?? 1, w: rr(6.6, 7.6), d: rr(5.0, 5.6), flowers: true });
  const K = main.kit;
  const wingW = rr(4.4, 5.2), wingD = rr(5.4, 6.4);
  const wallCol = opts.wall ?? pick(PALETTE.walls);
  const wing = new Kit();
  const wH = 2.3, roofH = 1.6;
  const wy = 0.5 + wH;
  const roof = opts.roof ?? pick(PALETTE.roofs);
  wing.body.box(wingW + 0.3, 1.2, wingD + 0.3, 0, -0.7, 0, '#9a9285', { jitter: 0.1 });
  wing.body.box(wingW, wH, wingD, 0, 0.5, 0, wallCol);
  wing.body.prism(wingW, roofH, wingD, 0, wy, 0, wallCol);
  gableRoof(wing, wingW, wingD, wy, roofH, roof, '#fbf6ea');
  addWindow(wing, 0, 0.5 + 1.5, wingD / 2, 0, { shutter: null, flowers: true });
  addWindow(wing, -wingW / 2, 0.5 + 1.5, 0, -Math.PI / 2, { shutter: null });
  // attach wing at the back-right, ridge perpendicular
  const ox = main.w / 2 + wingD / 2 - 1.2;
  const oz = -main.d / 2 + wingW / 2 + 0.2;
  K.absorb(wing, ox, 0, oz, Math.PI / 2);
  return { ...main, footprintW: main.w + wingD - 1.2, footprintD: Math.max(main.d, wingW + 0.4) };
}

/** Church with tower + spire. Front door at +Z. */
export function buildChurch() {
  const K = new Kit();
  const w = 7.2, d = 13.5, H = 5.6, roofH = 3.3;
  const wall = '#f1e8d6', roof = '#5a6a8a', stone = '#b4ab9c';
  K.body.box(w + 0.4, 1.2, d + 0.4, 0, -0.7, 0, stone, { jitter: 0.08 });
  K.body.box(w, H, d, 0, 0.5, 0, wall, { jitter: 0.03 });
  K.body.prism(w, roofH, d, 0, 0.5 + H, 0, wall);
  gableRoof(K, w, d, 0.5 + H, roofH, roof, '#e8dfcf');
  // buttresses + stained-glass windows on both sides
  const glass = [[1, 0.62, 0.45], [0.55, 0.8, 1], [1, 0.85, 0.4], [0.8, 0.65, 1]];
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 5; i++) K.body.box(0.6, 4.6, 0.7, sx * (w / 2 + 0.2), 0.3, -6.8 + i * 3.4, stone);
    for (let i = 0; i < 4; i++) {
      const z = -5.1 + i * 3.4;
      K.lit.box(0.12, 2.6, 1.0, sx * (w / 2 + 0.04), 2.2, z, glass[(i + (sx > 0 ? 1 : 0)) % 4]);
      K.body.box(0.2, 0.18, 1.3, sx * (w / 2 + 0.06), 4.8, z, '#e8dfcf');
      K.body.box(0.2, 0.2, 1.3, sx * (w / 2 + 0.06), 1.95, z, '#d8d0c0');
    }
  }
  // tower at the front
  const tw = 4.6, tz = d / 2 + tw / 2 - 1.2, TH = 11.5;
  K.body.box(tw + 0.4, 1.2, tw + 0.4, 0, -0.7, tz, stone, { jitter: 0.08 });
  K.body.box(tw, TH, tw, 0, 0.5, tz, wall, { jitter: 0.03 });
  K.body.box(tw + 0.5, 0.35, tw + 0.5, 0, 0.5 + TH - 1.7, tz, '#d8d0c0');
  K.body.box(tw + 0.5, 0.35, tw + 0.5, 0, 0.5 + TH, tz, '#d8d0c0');
  // belfry openings (dark) on 4 sides
  for (let s = 0; s < 4; s++) {
    const a = (s * Math.PI) / 2;
    const [dx, dz] = rot(0, tw / 2 + 0.02, a);
    K.body.box(1.3, 2.0, 0.12, dx, 0.5 + TH - 3.8, tz + dz, '#2c2f45', { ry: a });
  }
  // spire
  K.body.cone(tw * 0.78, 6.4, 4, 0, 0.5 + TH + 0.35, tz, roof, { ry: Math.PI / 4, jitter: 0.08 });
  K.body.box(0.14, 1.3, 0.14, 0, 0.5 + TH + 6.7, tz, '#e8c14a');
  K.body.box(0.7, 0.14, 0.14, 0, 0.5 + TH + 7.5, tz, '#e8c14a');
  // clock faces
  for (const [a, ox, oz] of [[0, 0, 1], [Math.PI / 2, 1, 0], [-Math.PI / 2, -1, 0]]) {
    const cx = ox * (tw / 2 + 0.08), cz = tz + oz * (tw / 2 + 0.08);
    K.body.cyl(0.85, 0.85, 0.12, 14, cx, 0.5 + TH - 5.3, cz, '#fff8e6', { rx: Math.PI / 2, ry: a });
    K.body.box(0.09, 0.62, 0.05, cx + ox * 0.08, 0.5 + TH - 5.3, cz + oz * 0.08, '#2c2f45', { ry: a });
    K.body.box(0.5, 0.09, 0.05, cx + ox * 0.08, 0.5 + TH - 5.3, cz + oz * 0.08, '#2c2f45', { ry: a });
  }
  // big front door + rose window
  K.body.box(2.2, 3.5, 0.2, 0, 0.5, tz + tw / 2 + 0.02, '#6b3f2a');
  K.body.box(2.6, 0.3, 0.3, 0, 4.0, tz + tw / 2 + 0.02, '#d8d0c0');
  K.body.box(2.9, 0.35, 1.0, 0, 0.3, tz + tw / 2 + 0.4, '#b7afa1');
  K.body.box(3.3, 0.35, 1.7, 0, 0.0, tz + tw / 2 + 0.85, '#b7afa1');
  K.lit.cyl(0.9, 0.9, 0.1, 12, 0, 6.6, tz + tw / 2 + 0.03, [1, 1, 1], { rx: Math.PI / 2 });
  K.body.cyl(1.1, 1.1, 0.08, 12, 0, 6.6, tz + tw / 2 + 0.0, '#e8dfcf', { rx: Math.PI / 2 });
  return { kit: K, w: w + 1.2, d: d + tw, height: 0.5 + TH + 8, spire: { x: 0, y: 0.5 + TH + 8, z: tz } };
}

/** Red barn + silo + hay. Front (doors) = +Z. */
export function buildBarn() {
  const K = new Kit();
  const w = 9.5, d = 7.2, H = 4.2, roofH = 3.0;
  const red = '#c9453a', white = '#fbf3e4', roof = '#6b5a55';
  K.body.box(w + 0.3, 1.0, d + 0.3, 0, -0.6, 0, '#8d867c', { jitter: 0.1 });
  K.body.box(w, H, d, 0, 0.4, 0, red, { jitter: 0.05 });
  K.body.prism(w, roofH, d, 0, 0.4 + H, 0, red);
  gableRoof(K, w, d, 0.4 + H, roofH, roof, white);
  // corner boards + doors
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) K.body.box(0.3, H, 0.3, sx * (w / 2), 0.4, sz * (d / 2), white);
  const dz = d / 2 + 0.03;
  K.body.box(3.9, 3.7, 0.12, 0, 0.4, dz, white);
  K.body.box(3.5, 3.4, 0.14, 0, 0.4, dz + 0.02, '#b53b31');
  for (const s of [-1, 1]) {
    K.body.boxC(0.2, Math.hypot(1.75, 3.4), 0.05, 0, 0.4 + 1.7, dz + 0.08, white, { rz: s * Math.atan2(1.75, 3.4) });
  }
  K.body.box(0.14, 3.4, 0.05, 0, 0.4, dz + 0.09, white);
  // hayloft
  K.body.box(1.9, 1.9, 0.12, 0, 0.4 + H + 0.3, dz, white);
  K.dark.box(1.5, 1.5, 0.1, 0, 0.4 + H + 0.5, dz + 0.04, [1, 1, 1]);
  // side windows
  for (const sx of [-1, 1]) for (const z of [-2, 2]) addWindow(K, sx * w / 2, 3.0, z, sx * Math.PI / 2, { on: chance(0.4), shutter: white, w: 0.8, h: 0.9 });
  return { kit: K, w: w + 1, d: d + 1, height: 0.4 + H + roofH };
}

export function buildSilo() {
  const K = new Kit();
  K.body.cyl(1.9, 2.05, 10, 12, 0, -0.4, 0, '#cfd8dc', { jitter: 0.04 });
  for (let i = 1; i < 6; i++) K.body.cyl(2.03, 2.03, 0.14, 12, 0, -0.4 + i * 1.65, 0, '#8d99a0');
  K.body.sphere(1.9, 0, 9.55, 0, '#8896a0', { sy: 0.7, jitter: 0.06 });
  K.body.box(0.14, 1.0, 0.14, 0, 11.0, 0, '#8896a0');
  K.body.box(0.7, 1.6, 0.06, 0, -0.4, 1.9, '#5b3a29');
  return { kit: K, r: 2.2 };
}

export function buildWindmillBody() {
  const K = new Kit();
  K.body.cyl(2.5, 2.9, 2.5, 8, 0, -1.2, 0, '#a8523d', { jitter: 0.1 });
  K.body.cyl(1.85, 2.6, 6.8, 8, 0, 1.3, 0, '#f4ead2', { jitter: 0.03 });
  K.body.cyl(2.75, 2.75, 0.18, 8, 0, 4.6, 0, '#7a4f33');
  K.body.cyl(0.0, 2.5, 2.6, 8, 0, 8.05, 0, '#8a3f34', { jitter: 0.08, ry: Math.PI / 8 });
  K.body.cyl(2.0, 2.0, 0.3, 8, 0, 8.0, 0, '#5b3a29');
  K.body.box(1.2, 2.2, 0.12, 0, -0.1, 2.72, '#6b3f2a');
  addWindow(K, 0, 5.6, 2.15, 0, { on: chance(0.6), shutter: '#6b3f2a', w: 0.7, h: 0.9 });
  addWindow(K, 2.0, 5.6, 0.0, Math.PI / 2, { on: true, shutter: '#6b3f2a', w: 0.7, h: 0.9 });
  addWindow(K, -2.0, 5.6, 0.0, -Math.PI / 2, { on: false, shutter: '#6b3f2a', w: 0.7, h: 0.9 });
  // axle
  K.body.cyl(0.24, 0.24, 2.2, 8, 0, 7.4, 1.6, '#4a3526', { rx: Math.PI / 2 });
  return { kit: K, hubY: 7.4, hubZ: 2.9 };
}

/** Rotating sails; the mesh is rotated around Z at the hub. */
export function buildWindmillRotor() {
  const b = new GeoBuilder();
  b.sphere(0.55, 0, 0, 0.1, '#5b3a29', { sz: 1.2 });
  for (let k = 0; k < 4; k++) {
    const a = (k * Math.PI) / 2;
    const R = (ox, oy) => [ox * Math.cos(a) - oy * Math.sin(a), ox * Math.sin(a) + oy * Math.cos(a)];
    // spar
    b.box(0.26, 8.6, 0.2, 0, 0, 0.0, '#6b4a33', { rz: a });
    // sail (cloth) + slats
    let [sx, sy] = R(0.95, 2.4);
    b.box(1.5, 5.6, 0.07, sx, sy, 0.16, '#f6efdc', { rz: a, jitter: 0.03 });
    [sx, sy] = R(0.1, 2.4);
    b.box(0.12, 5.6, 0.14, sx, sy, 0.14, '#6b4a33', { rz: a });
    [sx, sy] = R(1.75, 2.4);
    b.box(0.1, 5.6, 0.12, sx, sy, 0.14, '#6b4a33', { rz: a });
    for (let i = 0; i < 6; i++) {
      [sx, sy] = R(0.95, 2.4 + i * 1.1);
      b.box(1.6, 0.08, 0.12, sx, sy, 0.15, '#6b4a33', { rz: a });
    }
  }
  return b.build();
}

/** Well with little roof. */
export function buildWell() {
  const K = new Kit();
  K.body.cyl(1.05, 1.15, 0.95, 10, 0, 0, 0, '#a9a294', { jitter: 0.12 });
  K.body.cyl(0.88, 0.88, 0.05, 10, 0, 0.9, 0, '#1d3552');
  for (const s of [-1, 1]) K.body.box(0.14, 2.3, 0.14, s * 0.95, 0.5, 0, '#6b4a33');
  K.body.box(0.12, 0.12, 2.1, 0, 2.55, 0, '#6b4a33', { ry: 0 });
  K.body.box(0.12, 0.12, 2.1, 0, 2.55, 0, '#6b4a33', { ry: 0 });
  K.body.prism(2.5, 0.95, 1.8, 0, 2.6, 0, '#b9483c', { ry: Math.PI / 2 });
  K.body.cyl(0.18, 0.2, 0.3, 6, 0.0, 1.5, 0, '#8b5a3c');
  return { kit: K, r: 1.6 };
}
